import { PlayCompanionSchema, PlaySceneSchema, type Person, type PlayableRunState, type RunState, type StoryCommand } from '../../../packages/contracts/src/index.ts';
import { hash, playableState, reconcilePlayableState, validateLedger, type LifeInitializer } from '../../../packages/sim/src/index.ts';
import { type CombatState } from '../../../packages/sim/src/combat/index.ts';
import { allocateItem } from '../../../packages/sim/src/continuity/ledger.ts';
import { equip, instantiate, itemDefinition } from '../../../packages/sim/src/items/index.ts';
import type { NavPosition } from '../../../packages/sim/src/navigation/index.ts';
import { applyOrigin } from '../../../packages/sim/src/progression/origins.ts';
import { SLICE_MILESTONES } from '../../../packages/sim/src/campaign/slice.ts';
import { createStorySession, migrateStory, type StorySession } from '../../../packages/sim/src/campaign/story.ts';
import { arrivalEntry, freeSuccessorName, lifeDeeds, pastLives, reopenUnderContract, WORK_REOPENED } from '../../../packages/sim/src/campaign/story-legacy.ts';
import { applyEffects } from '../../../packages/sim/src/quests/graph.ts';
import { mysteryOpeningFacts, recordMystery } from '../../../packages/sim/src/campaign/mystery.ts';
import { areaFor, BEAT_POINTS, CONTENT, exploration } from './campaign.ts';
import { AREAS, MAPS, toPixel } from './painted-world.ts';
import { PC_ID } from './scene.ts';

export type HostPending = { destination: NavPosition; command: StoryCommand } | null;
/** Presentation-only scene corpse: the area it lies in and where, so a revisit can restore it. */
export type HostCorpse = { area: string; at: readonly [number, number] };
export type HostCheckpoint = { story: StorySession; combat: CombatState; pending: HostPending; corpses: Record<string, HostCorpse> };

type Candidate = StorySession['candidates'][number];
type StoredCandidate = Omit<Candidate, 'person' | 'contract'> & { personId: string; contract?: Candidate['contract'] };
type StoredEvidence = Pick<StorySession['evidence'][number], 'id' | 'caseId' | 'kind' | 'witnessId'>;
type StoredDialogue = StorySession['dialogue'];
type StoredStory = Record<string, unknown> & {
  slice: Omit<StorySession['slice'], 'continuity' | 'quest' | 'outcomeId'>;
  candidates: StoredCandidate[];
  dialoguePresentation: StoredDialogue;
  evidenceDetails: StoredEvidence[];
  combatActive: boolean;
};
type StoredShell = { version: 1; story: StoredStory; pending: HostPending; corpses?: Record<string, HostCorpse> };

const SYSTEM_KEY = 'shell';
const STORY_JOURNAL = 'shell-story:';
const DECIMAL_KEY = '$shellDecimal';

/** Browser-safe 256-bit content identity; four domain-separated simulation hashes. */
export const shellContentHash = Array.from({ length: 4 }, (_, part) => hash({ schema: 'shell-v1', part, content: CONTENT, areas: AREAS })).join('');

/** The seed the shell deals the next life of this save. The first life is the fixed
 * `reference` opening; from the second life on, the seed is dealt from the account
 * that just closed — its closed runs, dead, possessions and bequest — so two saves
 * that have lived differently are dealt different worlds, while the same closed
 * account, reopened, is always dealt the same one. */
export function nextLifeSeed(state: PlayableRunState): string {
  return `life-${state.envelope.runOrdinal + 1}-${hash(state.continuity)}`;
}

function replaceId<T>(input: T, from: string, to: string): T {
  // Built once per walk: a whole save passes through here several times on every scene change.
  const aliases = new Map([
    [from, to],
    [`remains:${from.slice('person:'.length)}`, `remains:${to.slice('person:'.length)}`],
    [`presentation:${from}`, `presentation:${to}`],
  ]);
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      const exact = aliases.get(value);
      if (exact) return exact;
      if (value.endsWith(from) && /[:-]$/.test(value.slice(0, -from.length))) return `${value.slice(0, -from.length)}${to}`;
      return value;
    }
    if (Array.isArray(value)) return value.map(walk);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [walk(key), walk(entry)]));
  };
  return walk(input) as T;
}

function encodeDecimals<T>(input: T): T {
  if (typeof input === 'number' && !Number.isInteger(input)) {
    if (!Number.isFinite(input)) throw new Error('Shell checkpoints cannot store a non-finite number.');
    return { [DECIMAL_KEY]: String(input) } as T;
  }
  if (Array.isArray(input)) return input.map(encodeDecimals) as T;
  if (!input || typeof input !== 'object') return input;
  return Object.fromEntries(Object.entries(input).map(([key, value]) => [key, encodeDecimals(value)])) as T;
}

function decodeDecimals<T>(input: T): T {
  if (Array.isArray(input)) return input.map(decodeDecimals) as T;
  if (!input || typeof input !== 'object') return input;
  const entries = Object.entries(input);
  if (entries.length === 1 && entries[0]![0] === DECIMAL_KEY && typeof entries[0]![1] === 'string') {
    const encoded = entries[0]![1];
    const value = Number(encoded);
    if (!Number.isFinite(value) || Number.isInteger(value) || String(value) !== encoded) throw new Error('Shell checkpoint contains an invalid number.');
    return value as T;
  }
  return Object.fromEntries(entries.map(([key, value]) => [key, decodeDecimals(value)])) as T;
}

function storedShell(state: PlayableRunState): StoredShell {
  const value = state.campaign.systems[SYSTEM_KEY];
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('version' in value) || value.version !== 1 || !('story' in value)) {
    throw new Error('This save has no playable shell checkpoint.');
  }
  return decodeDecimals(value as unknown as StoredShell);
}

function speakerPersonId(story: StorySession): string {
  const speakerId = story.dialogue!.speakerId;
  const scene = CONTENT.scenes.find(candidate => candidate.id === speakerId);
  if (scene?.personId === 'official') return story.politics.official.id;
  return scene?.personId ?? story.candidates.find(candidate => candidate.person.id === speakerId)?.person.id ?? speakerId;
}

/** Restore the story module's fixed PC alias from the current durable life identity. */
export function readHost(state: PlayableRunState): HostCheckpoint {
  const shell = storedShell(state);
  const combat = state.world.scenes[state.world.activeSceneId]!.combat as CombatState;
  const stored = shell.story;
  const details = new Map(stored.evidenceDetails.map(detail => [detail.id, detail]));
  const candidates = stored.candidates.map(candidate => {
    const person = state.continuity.persons[candidate.personId];
    const contract = state.companions[candidate.personId] ?? candidate.contract;
    if (!person || !contract) throw new Error(`Saved companion candidate ${candidate.personId} is incomplete.`);
    return { templateId: candidate.templateId, name: candidate.name, person, contract };
  });
  const {
    slice, candidates: _candidates, dialoguePresentation, evidenceDetails: _evidenceDetails,
    combatActive, ...storyFields
  } = stored;
  const durableStory = {
    ...storyFields,
    seed: state.envelope.worldSeed,
    originId: state.envelope.originId,
    purseCuts: state.money,
    slice: { ...slice, continuity: state.continuity, quest: state.quests, outcomeId: state.campaign.outcomeId },
    candidates,
    companionIds: Object.keys(state.companions),
    journal: state.journal.filter(entry => entry.sourceId?.startsWith(STORY_JOURNAL)).map(entry => ({
      id: entry.id,
      kind: entry.sourceId!.slice(STORY_JOURNAL.length) as StorySession['journal'][number]['kind'],
      text: entry.text,
    })),
    dialogue: state.dialogue ? dialoguePresentation : null,
    evidence: state.evidence.map(record => {
      const detail = details.get(record.id);
      if (!detail) throw new Error(`Saved story evidence ${record.id} is incomplete.`);
      return { ...detail, sourceKey: record.sourceId, finding: record.finding, verified: record.verified };
    }),
    combat: combatActive ? combat : null,
  } as unknown as StorySession;
  return replaceId({ story: durableStory, combat, pending: shell.pending, corpses: shell.corpses ?? {} }, state.pcId, PC_ID);
}

/** Scene corpses carried forward by the save alone; an unwritten save has none. Old saves read as none. */
function priorCorpses(state: PlayableRunState): Record<string, HostCorpse> {
  const value = state.campaign.systems[SYSTEM_KEY];
  if (!value || typeof value !== 'object' || Array.isArray(value) || (value as { version?: unknown }).version !== 1) return {};
  const corpses = (value as unknown as { corpses?: Record<string, HostCorpse> }).corpses;
  return corpses ? decodeDecimals(corpses) : {};
}

/** A life saved under an earlier content bundle continues under this one (run-store `migrateContent`). */
export function migrateShell(state: PlayableRunState): PlayableRunState {
  const host = readHost(state);
  host.story.slice = { ...host.story.slice, quest: applyEffects(host.story.slice.quest, { add: mysteryOpeningFacts(state), remove: [] }) };
  return writeHost(state, migrateStory(CONTENT, host.story), host.combat, host.pending);
}

/** Project one host result into the single canonical playable save. */
export function writeHost(state: PlayableRunState, story: StorySession, combat: CombatState, pending: HostPending): PlayableRunState {
  return reconcilePlayableState(projectHost(state, story, combat, pending));
}
/** writeHost before reconciliation: for a run-store commit, which reconciles its reducer's result itself. */
export function projectHost(state: PlayableRunState, story: StorySession, combat: CombatState, pending: HostPending): PlayableRunState {
  const next = structuredClone(state);
  const priorCombat = state.world.scenes[state.world.activeSceneId]!.combat as CombatState;
  const durableStory = replaceId(story, PC_ID, state.pcId);
  const durableCombat = replaceId(combat, PC_ID, state.pcId);
  const durablePending = replaceId(pending, PC_ID, state.pcId);
  next.continuity = validateLedger(durableStory.slice.continuity);
  // The survivor of this life must remember what was actually done, not only
  // what was verified. Completed story acts ride on the living PC; promotion
  // freezes them into the original audit at closure.
  const pc = next.continuity.persons[state.pcId];
  if (pc && pc.lifecycle !== 'dead') {
    pc.current.deeds = lifeDeeds(CONTENT, durableStory, pc.current.deeds);
    const summary = durableStory.slice.report?.personalGoal.summary;
    if (summary) pc.current.personalOutcome = summary;
  }
  if (durableCombat.tactics) durableCombat.tactics.continuity = structuredClone(next.continuity);
  const area = areaFor(durableStory.beatId);
  next.world = { activeSceneId: area.id, scenes: { [area.id]: PlaySceneSchema.parse({
    zoneId: CONTENT.beats.find(beat => beat.id === durableStory.beatId)!.zone,
    navigation: MAPS[area.id]!, combat: durableCombat,
  }) } };
  next.money = durableStory.purseCuts;
  next.tick += Math.max(0, durableCombat.tick - priorCombat.tick);
  next.quests = structuredClone(durableStory.slice.quest) as PlayableRunState['quests'];
  next.campaign.outcomeId = durableStory.slice.outcomeId;
  next.campaign.milestones = [...new Set([
    ...(durableStory.slice.report?.completedMilestones ?? []),
    ...SLICE_MILESTONES.filter(milestone => durableStory.slice.quest.completedNodeIds.some(id => id === milestone || id.startsWith(`${milestone}-`))),
  ])];
  next.companions = Object.fromEntries(durableStory.candidates
    .filter(candidate => durableStory.companionIds.includes(candidate.person.id))
    .map(candidate => [candidate.person.id, PlayCompanionSchema.parse(candidate.contract)]));
  next.dialogue = durableStory.dialogue ? {
    speakerId: speakerPersonId(durableStory),
    nodeId: durableStory.dialogue.lines.at(-1)?.textId ?? durableStory.dialogue.speakerId,
    pausedBefore: durableCombat.paused,
  } : null;
  next.journal = [
    ...next.journal.filter(entry => !entry.sourceId?.startsWith(STORY_JOURNAL)),
    ...durableStory.journal.map(entry => ({ id: entry.id, tick: next.tick, kind: 'event' as const, text: entry.text, sourceId: `${STORY_JOURNAL}${entry.kind}` })),
  ];
  next.evidence = durableStory.evidence.map(record => ({
    id: record.id, finding: record.finding, sourceId: record.sourceKey, verified: record.verified,
    proofId: next.evidence.find(existing => existing.id === record.id)?.proofId ?? null,
  }));
  next.paused = durableCombat.paused;

  const {
    slice, candidates, dialogue, evidence, combat: _combat, journal, purseCuts: _purseCuts,
    seed: _seed, originId: _originId, companionIds: _companionIds, ...rest
  } = durableStory;
  const { continuity: _continuity, quest: _quest, outcomeId: _outcomeId, ...storedSlice } = slice;
  // Scene corpses are presentation only: rebuild them from the dead in this area's combat,
  // carry earlier areas' corpses forward, and drop any body buried, animated or destroyed
  // so a revisit restores exactly the remains that are still exposed (AUT-12684).
  const corpses: Record<string, HostCorpse> = { ...priorCorpses(state) };
  for (const actor of Object.values(durableCombat.actors)) {
    if (actor.status === 'dead') corpses[actor.id] = { area: area.id, at: toPixel(actor.position) };
  }
  for (const id of Object.keys(corpses)) {
    const remainsId = `remains:${id.startsWith('body:') ? id.slice('body:'.length) : id.slice('person:'.length)}`;
    if (next.continuity.remains[remainsId]?.disposition !== 'exposed') delete corpses[id];
  }
  const stored: StoredShell = {
    version: 1,
    corpses,
    story: {
      ...rest,
      slice: storedSlice,
      candidates: candidates.map(candidate => ({
        templateId: candidate.templateId,
        name: candidate.name,
        personId: candidate.person.id,
        ...(durableStory.companionIds.includes(candidate.person.id) ? {} : { contract: candidate.contract }),
      })),
      dialoguePresentation: dialogue,
      evidenceDetails: evidence.map(({ id, caseId, kind, witnessId }) => ({ id, caseId, kind, witnessId })),
      combatActive: durableStory.combat !== null,
    },
    pending: durablePending,
  };
  next.campaign.systems = { ...next.campaign.systems, [SYSTEM_KEY]: encodeDecimals(stored) as unknown as PlayableRunState['campaign']['systems'][string] };
  recordMystery(CONTENT, state, next, durableStory);
  // Settlement changes the ledger after the ordinary tactical projection above.
  if (next.continuity.settlement === 'settled' && durableCombat.tactics) next.world.scenes[area.id]!.combat.tactics!.continuity = structuredClone(next.continuity);
  return next;
}

function prepareCompanionKit(story: StorySession): void {
  let continuity = story.slice.continuity;
  for (const candidate of story.candidates.filter(value => value.templateId === 'former-mortuary-guard')) {
    candidate.contract.obligations = candidate.contract.obligations.map(duty => ({ ...duty,
      against: Object.keys(continuity.remains).map(id => `body:${id}`),
    }));
    for (const template of ['spear-bronze', 'shield-round'] as const) {
      const item = instantiate(itemDefinition(template), `shell-${candidate.person.id.slice('person:'.length)}`, candidate.person.id);
      continuity = equip(allocateItem(continuity, item), candidate.person.id, item.id);
    }
  }
  story.slice = { ...story.slice, continuity };
}

function mergeOpening(run: RunState, story: StorySession): RunState {
  const imported = replaceId(story.slice.continuity, PC_ID, run.pcId);
  const continuity = structuredClone(run.continuity);
  const runId = `run:${run.envelope.runOrdinal}`;
  const presentationKeys = new Set(Object.values(continuity.persons).map(person => person.presentationKey));
  for (const [id, value] of Object.entries(imported.persons)) {
    const existing = continuity.persons[id];
    // A living returner takes their place in this opening (a hireling is back at the market, not
    // wherever the last life left them). The dead and promoted survivors keep their own state.
    if (existing && existing.lifecycle === 'active' && !existing.original) existing.location = value.location;
    if (id === run.pcId || existing) continue;
    const person = structuredClone(value);
    person.current.originalRunId = runId;
    person.current.latestRunId = runId;
    if (presentationKeys.has(person.presentationKey)) person.presentationKey = `presentation:${id}`;
    presentationKeys.add(person.presentationKey);
    continuity.persons[id] = person;
  }
  for (const [id, value] of Object.entries(imported.remains)) if (!continuity.remains[id]) continuity.remains[id] = structuredClone(value);
  for (const [id, value] of Object.entries(imported.items)) {
    if (continuity.items[id]) continue;
    if (value.allocation.kind === 'person' && continuity.persons[value.allocation.id]?.lifecycle === 'dead') continue;
    continuity.items[id] = structuredClone(value);
  }
  return { ...run, continuity: validateLedger(continuity) };
}

function reconcileOpeningOperation(story: StorySession): void {
  const continuity = story.slice.continuity;
  const operation = structuredClone(story.slice.operation);
  const archival = [] as typeof operation.storage;
  for (const station of operation.stations) {
    station.bodies = station.bodies.filter(body => {
      const disposition = continuity.remains[body.remainsId]?.disposition;
      if (disposition === 'animated') return true;
      if (disposition === 'exposed') { body.status = 'stilled'; return true; }
      archival.push({ remainsId: body.remainsId, position: body.position });
      return false;
    });
    if (!station.bodies.some(body => body.status === 'active')) station.integrity = 'released';
  }
  const viable = operation.storage.filter(body => {
    const remains = continuity.remains[body.remainsId];
    return remains?.disposition === 'exposed' && !remains.fixedBurial;
  });
  const unavailable = operation.storage.filter(body => !viable.some(candidate => candidate.remainsId === body.remainsId));
  // Storage is also the operation's accounting ledger. Keep unavailable records
  // after viable bodies so final accounting retains all nine without reanimating one.
  operation.storage = [...viable, ...archival, ...unavailable];
  // The dead an earlier life stopped stay stopped. Where the concession has set other dead to work, the world says it reopened.
  const reopened = operation.stations.some(station => station.integrity === 'intact')
    && (archival.length > 0 || operation.stations.some(station => station.bodies.some(body => body.status === 'stilled')));
  story.slice = { ...story.slice, operation, ...(reopened ? { quest: applyEffects(story.slice.quest, { add: [WORK_REOPENED], remove: [] }) } : {}) };
}

/** D10: the dead the account names are those this life met or killed — a death event in
 *  this run's ledger. The pre-buried and the concession's replacement dead arrive already
 *  dead and never were alive to meet, so the card counts them as the Stair's, never names them. */
export function fallenThisLife(state: PlayableRunState): { named: Person[]; others: number } {
  const diedInPlay = new Set(state.events.filter(event => event.type === 'death').map(event => event.personId));
  const fallen = Object.values(state.continuity.persons).filter(p => p.lifecycle === 'dead' && state.frozenContinuity.persons[p.id]?.lifecycle !== 'dead');
  const named = fallen.filter(p => diedInPlay.has(p.id));
  return { named, others: fallen.length - named.length };
}

/** Living hirelings an earlier outsider hired wait at their own place again, whatever
 * roster this life's seed dealt: their service is on their own record, not on the
 * roster that first named them. One who walked out on that employer does not return. */
function returnFormerHirelings(story: StorySession): void {
  const templates = new Map([...Object.values(CONTENT.companions).flat(), ...(CONTENT.locals ?? [])].map(template => [template.person.id, template]));
  for (const template of templates.values()) {
    if (story.candidates.some(candidate => candidate.person.id === template.person.id)) continue;
    const person = story.slice.continuity.persons[template.person.id];
    if (!person || person.lifecycle === 'dead' || person.lifecycle === 'retired') continue;
    const hired = person.current.relationships.filter(record => record.fact === 'hired');
    const employer = hired.at(-1)?.personId;
    if (!employer || person.current.relationships.some(record => record.fact === 'departed' && record.personId === employer)) continue;
    person.lifecycle = 'active';
    person.location = template.person.location;
    story.candidates.push({ templateId: template.templateId, name: template.name, person, contract: structuredClone(template.contract) });
  }
}

/** Build the authored opening around the durable life allocated by the run store. */
export function createShellInitializer(name = ''): LifeInitializer {
  return input => {
    let run = { ...input, continuity: applyOrigin(input.continuity, input.pcId, input.envelope.originId, `life-${input.envelope.runOrdinal}`) };
    const pc = run.continuity.persons[run.pcId]!;
    const story = createStorySession(CONTENT, { seed: run.envelope.worldSeed, originId: run.envelope.originId, past: pastLives(run.continuity) });
    reopenUnderContract(CONTENT, story, run.continuity, input.envelope.runOrdinal);
    // A blank name is a generated one the ledger has never carried, and whose first name nobody met in this life
    // shares: not its cast, its hirelings or working dead, nor anyone still living from an earlier life.
    const first = (name: string): string => name.split(' ')[0]!;
    const others = Object.values(run.continuity.persons).filter(person => person.id !== pc.id), cast = [...Object.values(story.slice.continuity.persons), ...story.candidates.map(candidate => candidate.person)];
    const taken = new Set([...others, ...cast].map(person => person.current.name));
    const met = new Set([...cast, ...others.filter(person => person.lifecycle !== 'dead')].map(person => first(person.current.name)));
    // Step through the stock so successive outsiders do not share a first name.
    const stock = CONTENT.successorNames, start = input.envelope.runOrdinal * 11 % stock.length;
    // Past the authored stock the names are built from its parts; a first name nobody met is kept while one exists.
    const order = [...stock.slice(start), ...stock.slice(0, start)], unmet = stock.some(candidate => !met.has(first(candidate)));
    pc.current.name = name.trim() || freeSuccessorName(stock, order, candidate => !taken.has(candidate) && !(unmet && met.has(first(candidate))));
    // A new outsider arrives whole; the shared person template starts wounded.
    pc.current.hp = pc.current.maxHp;
    const entry = arrivalEntry(run.continuity, run.envelope.runOrdinal);
    if (entry) { story.journal.push(entry); story.events.push({ type: 'journal', sequence: story.events.length, entry }); }
    prepareCompanionKit(story);
    run = mergeOpening(run, story);
    story.slice = { ...story.slice, continuity: replaceId(run.continuity, run.pcId, PC_ID) };
    reconcileOpeningOperation(story);
    for (const candidate of story.candidates) candidate.person = story.slice.continuity.persons[candidate.person.id]!;
    returnFormerHirelings(story);
    const combat = exploration(story, BEAT_POINTS[story.beatId]!);
    const area = areaFor(story.beatId);
    const base = playableState(run, area.id, {
      zoneId: 'Z03', navigation: structuredClone(MAPS[area.id]!), combat: replaceId(combat, PC_ID, run.pcId),
    });
    story.slice = { ...story.slice, quest: applyEffects(story.slice.quest, { add: mysteryOpeningFacts(base), remove: [] }) };
    return writeHost(base, story, combat, null);
  };
}
